import { useState, useEffect, Fragment } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useProgress } from '../hooks/useProgress';
import { useAuth } from '../hooks/useAuth';
import { Nav } from '../components/Nav';
import { ProgressBar } from '../components/ProgressBar';
import { ScaleQuestion } from '../components/ScaleQuestion';
import { AudioPlayer } from '../components/AudioPlayer';
import { saveJournal, getJournal, saveCheckin, completeDay, saveMeditationProgress } from '../utils/api';
import {
  DAYS,
  JOURNAL_PROMPTS_BY_DAY,
  JOURNAL_INTROS_BY_DAY,
  CHECKIN_QUESTIONS_BY_DAY,
  CHECKIN_BANDS_BY_DAY,
  DAY6_SECTION_HEADERS,
  DAY6_PART_TWO_INTRO,
  MEDITATION_NAMES,
  C,
} from '../utils/constants';

export const DayJournal = () => {
  const { dayNum } = useParams();
  const dayNumber = parseInt(dayNum, 10);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getDayProgress, completedDays, refetch } = useProgress();

  const day = DAYS.find(d => d.num === dayNumber);
  const progressData = getDayProgress(dayNumber);

  // Per-day content
  const prompts      = JOURNAL_PROMPTS_BY_DAY[dayNumber]   || [];
  const journalIntro = JOURNAL_INTROS_BY_DAY[dayNumber]    || '';
  const questions    = CHECKIN_QUESTIONS_BY_DAY[dayNumber] || [];
  const bands        = CHECKIN_BANDS_BY_DAY[dayNumber]     || [];

  // Journal answers: dynamic length based on this day's prompt count
  const [answers, setAnswers] = useState(() => Array(prompts.length).fill(''));
  const [checkin, setCheckin] = useState({ q1: null, q2: null, q3: null });
  const [completing, setCompleting] = useState(false);
  const [loadingJournal, setLoadingJournal] = useState(true);
  const [journalLoadError, setJournalLoadError] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleAdmin = () => navigate('/admin');

  // Load existing journal — fill the dynamic-length answers array.
  // Until this resolves the form may not yet show the user's saved answers, so
  // the Complete button stays disabled (loadingJournal) to prevent submitting a
  // blank form over saved data. A load FAILURE must NOT silently enable submit:
  // we surface journalLoadError and keep Complete disabled (with a retry).
  const loadJournal = () => {
    setLoadingJournal(true);
    setJournalLoadError(false);
    getJournal(dayNumber)
      .then(res => {
        const entries = res.data?.entries;
        if (entries && Array.isArray(entries)) {
          const filled = Array(prompts.length).fill('');
          entries.forEach(e => {
            if (e.prompt_index >= 0 && e.prompt_index < prompts.length) {
              filled[e.prompt_index] = e.answer || '';
            }
          });
          setAnswers(filled);
        }
        setLoadingJournal(false);
      })
      .catch(() => {
        // Do NOT clear loadingJournal here: leaving the form un-loaded keeps the
        // Complete button disabled so a failed load can't be submitted as blanks.
        setJournalLoadError(true);
      });
  };
  useEffect(() => {
    loadJournal();
    // prompts.length is stable for a given dayNumber
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayNumber]);

  const allCheckinAnswered = questions.length > 0 && questions.every((_, i) => checkin[`q${i + 1}`] !== null);
  const totalCheckin = allCheckinAnswered
    ? questions.reduce((sum, _, i) => sum + checkin[`q${i + 1}`], 0)
    : null;

  const checkinEval = () => {
    if (totalCheckin === null || bands.length === 0) return null;
    const idx = totalCheckin <= 5 ? 0 : totalCheckin <= 10 ? 1 : 2;
    const colors = [C.terracotta, C.amber, C.gold];
    return { ...bands[idx], color: colors[idx] };
  };

  const evalResult = checkinEval();

  const handleComplete = async () => {
    setCompleting(true);
    try {
      // Save journal — one row per prompt, prompt_index = position in array
      const entries = answers.map((answer, i) => ({ prompt_index: i, answer }));
      await saveJournal(dayNumber, entries);

      // Save checkin if answered. q1/q2/q3 map positionally onto the legacy
      // calm/connected/clarity columns in ry_checkins.
      if (allCheckinAnswered) {
        await saveCheckin(dayNumber, checkin.q1, checkin.q2, checkin.q3);
      }

      // Complete day
      await completeDay(dayNumber);
      await refetch();

      // Day 6 — send to celebration flow
      if (dayNumber === 6) {
        navigate('/completion');
      } else {
        navigate('/dashboard');
      }
    } catch (e) {
      console.error('Error completing day:', e);
      setCompleting(false);
    }
  };

  // Step info
  const totalSteps = day?.hasMeditation ? 3 : 2;
  const stepNum = 2;

  if (!day) {
    return (
      <div style={{ minHeight: '100vh', background: C.cream }}>
        <Nav user={user} onAdmin={handleAdmin} />
        <div style={{ textAlign: 'center', padding: '80px 24px', color: C.terracotta }}>Day not found.</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: C.cream }}>
      <Nav user={user} onAdmin={handleAdmin} />
      <ProgressBar completedDays={completedDays} />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 16px 80px' }}>
        {/* Breadcrumb */}
        <nav style={{ fontSize: '0.8rem', color: C.terracotta, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Link to="/dashboard" style={{ color: C.terracotta, textDecoration: 'none' }}>Dashboard</Link>
          <span style={{ opacity: 0.5 }}>›</span>
          <span style={{ color: C.brown, fontWeight: 600 }}>Day {dayNumber} · Journal</span>
        </nav>

        {/* Header */}
        <div className="animate-in" style={{ marginBottom: 28 }}>
          <div style={{
            fontFamily: "'Forum', serif",
            fontSize: '0.7rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: C.gold,
            marginBottom: 6,
          }}>
            Step {stepNum} of {totalSteps}
          </div>
          <h1 style={{
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: 'clamp(1.4rem, 4vw, 1.9rem)',
            letterSpacing: '0.02em',
            marginBottom: 4,
          }}>
            Day {dayNumber}: {day.title}
          </h1>
          <p style={{ color: C.terracotta, fontSize: '0.88rem' }}>
            Reflect, journal, and complete today's practice
          </p>
        </div>

        {/* Day description — recap of what they just watched, sits above the check-in */}
        {day.description && (
          <section style={{
            background: '#fff',
            borderRadius: 14,
            padding: '20px 24px',
            border: `1.5px solid ${C.border}`,
            marginBottom: 24,
          }}>
            <p style={{ color: C.text, fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
              {day.description}
            </p>
          </section>
        )}

        {/* Check-in Section */}
        <section style={{
          background: '#fff',
          borderRadius: 14,
          padding: '24px 24px',
          border: `1.5px solid ${C.border}`,
          marginBottom: 24,
        }}>
          <h2 style={{
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: '1.1rem',
            letterSpacing: '0.03em',
            marginBottom: 6,
          }}>
            Mini Check-In
          </h2>
          <p style={{ color: C.terracotta, fontSize: '0.82rem', marginBottom: 20 }}>
            Rate each question from 0 (not at all) to 5 (completely).
          </p>

          {questions.map((q, i) => (
            <ScaleQuestion
              key={q.id}
              label={q.label}
              value={checkin[q.id]}
              onChange={val => setCheckin(prev => ({ ...prev, [q.id]: val }))}
            />
          ))}

          {/* Evaluation panel */}
          {allCheckinAnswered && evalResult && (
            <div style={{
              background: `${evalResult.color}14`,
              border: `1.5px solid ${evalResult.color}40`,
              borderRadius: 10,
              padding: '14px 18px',
              marginTop: 20,
              animation: 'fadeInUp 0.4s ease both',
            }}>
              <div style={{
                fontFamily: "'Forum', serif",
                color: evalResult.color,
                fontSize: '1rem',
                marginBottom: 4,
                letterSpacing: '0.03em',
              }}>
                {evalResult.label} · {totalCheckin}/15
              </div>
              <p style={{ fontSize: '0.85rem', color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {evalResult.message}
              </p>
            </div>
          )}
        </section>

        {/* Journal Prompts */}
        <section style={{
          background: '#fff',
          borderRadius: 14,
          padding: '24px 24px',
          border: `1.5px solid ${C.border}`,
          marginBottom: 24,
        }}>
          <h2 style={{
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: '1.1rem',
            letterSpacing: '0.03em',
            marginBottom: 6,
          }}>
            Journaling Prompts
          </h2>
          {journalIntro && (
            <p style={{ color: C.terracotta, fontSize: '0.82rem', lineHeight: 1.6, marginBottom: 24 }}>
              {journalIntro}
            </p>
          )}

          {prompts.map((prompt, i) => (
            <Fragment key={i}>
              {/* Day 6 section header — Part One */}
              {dayNumber === 6 && i === 0 && (
                <h3 style={{
                  fontFamily: "'Forum', serif",
                  color: C.brown,
                  fontSize: '1rem',
                  letterSpacing: '0.04em',
                  marginTop: 4,
                  marginBottom: 18,
                  paddingBottom: 8,
                  borderBottom: `1px solid ${C.border}`,
                }}>
                  {DAY6_SECTION_HEADERS.partOne}
                </h3>
              )}

              {/* Day 6 section header — Part Two + its intro */}
              {dayNumber === 6 && i === 7 && (
                <>
                  <h3 style={{
                    fontFamily: "'Forum', serif",
                    color: C.brown,
                    fontSize: '1rem',
                    letterSpacing: '0.04em',
                    marginTop: 32,
                    marginBottom: 12,
                    paddingBottom: 8,
                    borderBottom: `1px solid ${C.border}`,
                  }}>
                    {DAY6_SECTION_HEADERS.partTwo}
                  </h3>
                  <p style={{ color: C.terracotta, fontSize: '0.82rem', lineHeight: 1.6, marginBottom: 24 }}>
                    {DAY6_PART_TWO_INTRO}
                  </p>
                </>
              )}

              <div style={{ marginBottom: i < prompts.length - 1 ? 28 : 0 }}>
                {prompt.label ? (
                  // Labelled prompts (Day 6 — Event, Perspective, etc.) get the pill
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 8,
                  }}>
                    <span style={{
                      background: `linear-gradient(135deg, ${C.amber}, ${C.gold})`,
                      color: '#fff',
                      fontFamily: "'Forum', serif",
                      fontSize: '0.65rem',
                      padding: '2px 10px',
                      borderRadius: 20,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}>
                      {prompt.label}
                    </span>
                  </div>
                ) : (
                  // Days 1–5 — plain numbered
                  <div style={{
                    fontFamily: "'Forum', serif",
                    color: C.amber,
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    marginBottom: 6,
                  }}>
                    {i + 1}.
                  </div>
                )}
                <p style={{
                  fontSize: '0.88rem',
                  color: C.text,
                  lineHeight: 1.65,
                  marginBottom: 10,
                  fontStyle: 'italic',
                }}>
                  {prompt.q}
                </p>
                <textarea
                  value={answers[i] || ''}
                  onChange={e => {
                    const next = [...answers];
                    next[i] = e.target.value;
                    setAnswers(next);
                  }}
                  placeholder="Write freely here..."
                  rows={4}
                />
              </div>
            </Fragment>
          ))}
        </section>

        {/* Meditation section */}
        {day.hasMeditation && (
          <section style={{
            background: '#fff',
            borderRadius: 14,
            padding: '24px 24px',
            border: `1.5px solid ${C.border}`,
            marginBottom: 24,
          }}>
            <h2 style={{
              fontFamily: "'Forum', serif",
              color: C.brown,
              fontSize: '1.1rem',
              letterSpacing: '0.03em',
              marginBottom: 6,
            }}>
              Guided Meditation
            </h2>
            <p style={{ color: C.terracotta, fontSize: '0.82rem', marginBottom: 20 }}>
              Step {totalSteps} of {totalSteps} — Find somewhere quiet and close your eyes.
            </p>
            <AudioPlayer
              src={`/audio/day${dayNumber}-meditation.mp3`}
              title={MEDITATION_NAMES[dayNumber] || 'Guided Meditation'}
              onListened={() => saveMeditationProgress(dayNumber, 100).catch(() => {})}
            />
          </section>
        )}

        {/* Load-failure notice + retry — must not allow completing from a blank form */}
        {journalLoadError && (
          <div style={{ textAlign: 'center', marginBottom: 12 }}>
            <p style={{ fontSize: '0.85rem', color: C.terracotta, marginBottom: 8 }}>
              Couldn’t load your saved answers. Please retry before completing — completing now could overwrite saved work.
            </p>
            <button className="btn-secondary" onClick={loadJournal} style={{ padding: '8px 20px', fontSize: '0.85rem' }}>
              Retry loading
            </button>
          </div>
        )}

        {/* Complete button — disabled until existing answers have loaded, so a
            blank/half-loaded form can never be submitted over saved data. */}
        <button
          className="btn-primary"
          onClick={handleComplete}
          disabled={completing || loadingJournal || journalLoadError}
          style={{
            width: '100%',
            fontSize: '1rem',
            padding: '16px 32px',
          }}
        >
          {completing
            ? 'Completing...'
            : loadingJournal
              ? 'Loading your answers…'
              : journalLoadError
                ? 'Couldn’t load — retry above'
                : `Complete Day ${dayNumber} ✓`}
        </button>

        <p style={{ textAlign: 'center', marginTop: 12, fontSize: '0.78rem', color: C.terracotta, opacity: 0.7 }}>
          Your journal and check-in are saved automatically when you complete.
        </p>
      </div>
    </div>
  );
};
